import type React from 'react';
import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { WebSocketProvider } from '@zg-rentals/web-socket-react';
import { useAuth } from '../AuthProvider';
import { getInboxConversationId, getWebSocketTopicSuffixes } from '../utils/webSocketTopicSuffixes';
import { bootstrap } from '../bootstrap';
import useBooleanConfig from '../hooks/useBooleanConfig';
import useDevices from '../hooks/useDevices';

export const SatelliteWebSocketWrapper = ({
  isMobileDevice,
  children,
}: {
  isMobileDevice: boolean;
  children: React.ReactNode;
}) => {
  // Gate the socket on the login-aware `isAuthorized` from auth, not `!!user`: the user-info
  // endpoint returns a truthy object even for logged-out sessions, so `!!user` would open the
  // socket for anonymous users and the backend rejects the unauthenticated handshake.
  const { isAuthorized } = useAuth();
  const router = useRouter();
  const { isMobileView } = useDevices();
  const websocketUrl = process.env.NEXT_PUBLIC_WEBSOCKET_URL;
  const isWebSocketEnabled = useBooleanConfig('enableWebsocketIntegration', true) && !!websocketUrl;
  const listingAlias = router.query.listingAlias?.toString();
  const isLeadManagement = router.pathname.startsWith('/lead-management');
  const conversationId = isLeadManagement
    ? router.query.messagingConversationId?.toString()
    : getInboxConversationId(router.query.conversationSlug);

  const topicSuffixes = useMemo(
    () => getWebSocketTopicSuffixes({ listingAlias, conversationId, isLeadManagement }),
    [listingAlias, conversationId, isLeadManagement],
  );

  const webSocketLogger = useMemo(() => bootstrap.logger.child({ name: 'WebSocketConnection' }), []);
  const webSocketLoggerContext = useMemo(() => ({ isMobileDevice, isMobileView }), [isMobileDevice, isMobileView]);

  return (
    <WebSocketProvider
      topicSuffixes={topicSuffixes}
      websocketUrl={websocketUrl || ''}
      isAuthorized={isAuthorized}
      isEnabled={isWebSocketEnabled}
      debug={process.env.NEXT_PUBLIC_WEBSOCKET_DEBUG === 'true'}
      logger={webSocketLogger}
      loggerContext={webSocketLoggerContext}
    >
      {children}
    </WebSocketProvider>
  );
};
